import { useMemo, useState } from 'react';
import type { ChartResult } from '@astrally/shared';
import { computeReferenceChart } from '@astrally/astro-engine';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { Section } from '../../components/common';
import { WesternWheel } from './WesternWheel';

/**
 * The stability view — the heart of approximate/unknown modes.
 * Stable / Possible / Unavailable sections, Moon multi-position callout,
 * dasha possibilities, an interactive window control (approximate mode), and
 * an explicitly-labelled reference chart (unknown mode).
 */
export function UncertaintyView({ chart }: { chart: ChartResult }) {
  const { t } = useI18n();
  const u = chart.uncertainty!;
  const isUnknown = chart.meta.timeConfidence === 'unknown';

  const moonVarying = u.varying.find(
    (f) => f.id === 'planet.moon.siderealSign' || f.id === 'planet.moon.nakshatra',
  );

  return (
    <div className="col">
      {chart.meta.timeConfidence === 'approximate' && <WindowControl chart={chart} />}

      {moonVarying && (
        <div className="banner banner--gold">{t('chart.moon.multi')}</div>
      )}

      <Section title={isUnknown ? t('chart.stable.day') : t('chart.stable')}>
        <div className="card col" style={{ gap: 8 }}>
          {u.stable
            .filter((f) => !f.id.startsWith('western.aspect'))
            .map((f) => (
              <div key={f.id} className="spread">
                <span className="small muted">{f.label}</span>
                <span className="small" style={{ fontWeight: 600 }}>{f.value}</span>
              </div>
            ))}
          {u.stable.filter((f) => f.id.startsWith('western.aspect')).length > 0 && (
            <>
              <div className="divider" style={{ margin: '4px 0' }} />
              <div className="tiny faint">Aspects holding across the entire window:</div>
              <div className="wrap">
                {u.stable
                  .filter((f) => f.id.startsWith('western.aspect'))
                  .map((f) => (
                    <span key={f.id} className="chip" style={{ cursor: 'default' }}>
                      {f.value}
                    </span>
                  ))}
              </div>
            </>
          )}
        </div>
      </Section>

      {u.varying.length > 0 && (
        <Section title={t('chart.possible')}>
          <div className="col">
            {u.varying.map((f) => (
              <div key={f.id} className="card">
                <div className="h3" style={{ marginBottom: 8 }}>{f.label}</div>
                <div className="col" style={{ gap: 6 }}>
                  {f.possibilities.map((p, i) => (
                    <div key={i} className="spread">
                      <span className="small">{p.value}</span>
                      <span className="tiny muted">
                        {p.fromLocal}–{p.toLocal} · {(p.fraction * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
                {/* Probability bar */}
                <div className="row" style={{ gap: 2, marginTop: 8 }}>
                  {f.possibilities.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        flex: Math.max(p.fraction, 0.02),
                        height: 6,
                        borderRadius: 3,
                        background: i % 2 === 0 ? 'var(--accent)' : 'var(--gold)',
                        opacity: 0.7,
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {u.dasha && u.dasha.possibilities.length > 0 && (
        <Section title="Dasha possibilities at birth" term="dasha">
          <div className="card col" style={{ gap: 8 }}>
            {u.dasha.possibilities.map((d, i) => (
              <div key={i} className="spread">
                <span className="small">
                  <b style={{ textTransform: 'capitalize' }}>{d.mahadashaLord}</b> mahadasha
                  <span className="muted"> (Moon in {d.nakshatraName})</span>
                </span>
                <span className="tiny muted">{(d.fraction * 100).toFixed(0)}% of window</span>
              </div>
            ))}
            <div className="tiny faint">{u.dasha.note}</div>
          </div>
        </Section>
      )}

      <Section title={t('chart.unavailable')}>
        <div className="card col" style={{ gap: 8 }}>
          {u.unavailable.map((f) => (
            <div key={f.id}>
              <div className="small" style={{ fontWeight: 600 }}>{f.label}</div>
              <div className="tiny muted">{f.reason}</div>
            </div>
          ))}
        </div>
      </Section>

      {isUnknown && <ReferenceChartSection />}
    </div>
  );
}

/** Interactive time-window control: recalculates the chart live. */
function WindowControl({ chart }: { chart: ChartResult }) {
  const { t } = useI18n();
  const { recalcChart, birthProfile } = useApp();
  const timeInput = chart.birth.timeInput;
  const initial =
    timeInput.kind === 'approximate'
      ? timeInput.window
      : { start: { hour: 9, minute: 0 }, end: { hour: 12, minute: 0 } };
  const [startMin, setStartMin] = useState(initial.start.hour * 60 + initial.start.minute);
  const [endMin, setEndMin] = useState(initial.end.hour * 60 + initial.end.minute);
  const [busy, setBusy] = useState(false);

  const label = (m: number) =>
    `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  const apply = async () => {
    if (!birthProfile) return;
    setBusy(true);
    try {
      await recalcChart({
        ...birthProfile.input,
        timeInput: {
          kind: 'approximate',
          window: {
            start: { hour: Math.floor(startMin / 60), minute: startMin % 60 },
            end: { hour: Math.floor(endMin / 60), minute: endMin % 60 },
          },
          label: 'custom',
        },
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="h3" style={{ marginBottom: 8 }}>{t('chart.window.adjust')}</div>
      <div className="spread small muted" style={{ marginBottom: 4 }}>
        <span>{label(startMin)}</span>
        <span>{label(endMin)}</span>
      </div>
      {/* aria-valuetext, not just aria-label: the raw value is a minute count
          0–1439, so without it a screen reader announces "seven hundred and
          twenty" where the sighted user reads 12:00. */}
      <input
        type="range"
        min={0}
        max={1435}
        step={5}
        value={startMin}
        aria-label="Window start time"
        aria-valuetext={label(startMin)}
        onChange={(e) => setStartMin(Math.min(Number(e.target.value), endMin - 5))}
      />
      <input
        type="range"
        min={0}
        max={1439}
        step={5}
        value={endMin}
        aria-label="Window end time"
        aria-valuetext={label(endMin)}
        onChange={(e) => setEndMin(Math.max(Number(e.target.value), startMin + 5))}
      />
      <button className="btn btn--primary btn--small btn--block" style={{ marginTop: 8 }} disabled={busy} onClick={apply}>
        {busy ? <span className="spinner" /> : 'Recalculate with this window'}
      </button>
    </div>
  );
}

/** Sunrise/noon reference chart — explicitly labelled, opt-in, never the natal chart. */
function ReferenceChartSection() {
  const { t } = useI18n();
  const { birthProfile } = useApp();
  const [kind, setKind] = useState<'sunrise' | 'noon' | null>(null);

  const refChart = useMemo(() => {
    if (!kind || !birthProfile) return null;
    try {
      return computeReferenceChart(birthProfile.input, kind);
    } catch {
      return null;
    }
  }, [kind, birthProfile]);

  return (
    <Section title="Optional reference view">
      <div className="card col">
        <div className="tiny muted">
          A reference chart assumes a symbolic time (sunrise or noon). It can be useful for
          traditional reference-style readings but it is NOT your natal chart.
        </div>
        <div className="row">
          <button
            className={`chip ${kind === 'sunrise' ? 'chip--on' : ''}`}
            onClick={() => setKind(kind === 'sunrise' ? null : 'sunrise')}
          >
            Sunrise reference
          </button>
          <button
            className={`chip ${kind === 'noon' ? 'chip--on' : ''}`}
            onClick={() => setKind(kind === 'noon' ? null : 'noon')}
          >
            Noon reference
          </button>
        </div>
        {refChart && (
          <div className="col">
            <div className="banner banner--danger small">{t('chart.reference.label')}</div>
            <WesternWheel chart={refChart} />
          </div>
        )}
      </div>
    </Section>
  );
}
